/**
 * Premium Text Adapter for the design system
 * 
 * This component provides luxury typography styles with premium touches
 * like gradient text, animations, and subtle effects matching our million-dollar UI.
 */
'use client';

import React from 'react';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/lib/utils';

// Import types from the design system
import {
  TextVariant,
  TextSize,
  TextStyle,
  TextColor,
  TextAlign,
  TextWeight,
  TextBaseProps,
  HeadingBaseProps
} from '@repo/design-system/types/text';

/**
 * Premium Text component props
 */
export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, keyof TextBaseProps>, TextBaseProps {}

/**
 * Premium Heading component props
 */
export interface HeadingProps extends Omit<React.HTMLAttributes<HTMLElement>, keyof HeadingBaseProps>, HeadingBaseProps {}

/**
 * Map text variants to appropriate HTML elements
 */
const variantElementMap: Record<TextVariant, React.ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  body: 'p',
  'body-large': 'p',
  'body-small': 'p',
  caption: 'span',
  label: 'span',
  overline: 'span',
};

/**
 * Map text variants to class names for premium typography
 */
const variantClassMap: Record<TextVariant, string> = {
  h1: 'text-4xl font-bold tracking-tight',
  h2: 'text-3xl font-bold tracking-tight',
  h3: 'text-2xl font-semibold tracking-tight',
  h4: 'text-xl font-semibold',
  h5: 'text-lg font-medium',
  h6: 'text-base font-medium',
  body: 'text-base leading-relaxed',
  'body-large': 'text-lg leading-relaxed',
  'body-small': 'text-sm leading-relaxed',
  caption: 'text-sm',
  label: 'text-sm font-medium',
  overline: 'text-xs uppercase tracking-wider font-medium',
};

/**
 * Map text sizes to class names for more precision
 */
const sizeClassMap: Record<TextSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
  xl: 'text-xl',
  '2xl': 'text-2xl',
  '3xl': 'text-3xl',
  '4xl': 'text-4xl',
};

/**
 * Map text colors to class names with premium colors
 */
const colorClassMap: Record<TextColor, string> = {
  default: 'text-gray-900 dark:text-gray-100',
  primary: 'text-primary-600 dark:text-primary-400',
  secondary: 'text-gray-600 dark:text-gray-300',
  muted: 'text-gray-500 dark:text-gray-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
  info: 'text-primary-600 dark:text-primary-400',
};

/**
 * Map text styles to premium class names
 */
const styleClassMap: Record<TextStyle, string> = {
  default: '',
  gradient: 'premium-gradient-text',
  premium: 'premium-gradient-glow',
  gradientSubtle: 'premium-gradient-text-subtle',
  glow: 'text-primary-500 premium-text-glow dark:text-primary-400',
  outlined: 'premium-text-outlined'
};

/**
 * Map text weights to class names
 */
const weightClassMap: Record<TextWeight, string> = {
  light: 'font-light',
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

/**
 * Map text align to class names
 */
const alignClassMap: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

// Animation variants for text entrance
const textAnimationVariants: Variants = {
  hidden: { 
    opacity: 0,
    y: 10
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

/**
 * Premium Text component
 * 
 * This component provides luxury text styles with premium touches
 * like gradient text, animations, and subtle effects.
 * 
 * @example
 * ```tsx
 * <Text variant="body" style="gradient">Luxury Text</Text>
 * <Text variant="body-large" style="glow">Glowing Text</Text>
 * ```
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(
  (props, ref) => {
    const {
      variant = 'body',
      color = 'default',
      style = 'default',
      align,
      weight,
      className,
      as,
      children,
      truncate,
      size,
      animate = false,
      ...rest
    } = props;

    // Determine the element to render, defaulting to 'div' if undefined
    const Component = as || variantElementMap[variant] || 'div';

    // Prepare the common classnames
    const classNames = cn(
      // Base variant styling
      variant && variantClassMap[variant],
      
      // Override with size if specified
      size && sizeClassMap[size],
      
      // Color styling
      color && colorClassMap[color],
      
      // Special styling (gradient, glow, etc)
      style && styleClassMap[style],
      
      // Text alignment
      align && alignClassMap[align],
      
      // Font weight
      weight && weightClassMap[weight],
      
      // Truncate text if needed
      truncate && 'truncate',
      
      // Custom class names
      className
    );

    // Create the base component with safeguards
    try {
      const content = React.createElement(Component, {
        ref,
        className: classNames,
        ...rest,
        children
      });

      // If animation is enabled, wrap in motion component
      if (animate) {
        return (
          <motion.div
            className="inline-block"
            initial="hidden"
            animate="visible"
            variants={textAnimationVariants}
          >
            {content}
          </motion.div>
        );
      }

      // Otherwise return the base component
      return content;
    } catch (error) {
      console.error('Error rendering Text component:', error);
      // Fallback to a div if there's an error - use type assertion for ref
      return <div ref={ref as React.LegacyRef<HTMLDivElement>} className={classNames} {...rest}>{children}</div>;
    }
  }
);

Text.displayName = 'Text';

/**
 * Premium Heading component
 * 
 * This component provides luxury heading styles with gradient effects and animations.
 * 
 * @example
 * ```tsx
 * <Heading level={1} style="gradient" animate>Luxury Heading</Heading>
 * <Heading level={2} style="glow">Glowing Heading</Heading>
 * ```
 */
export const Heading: React.FC<HeadingProps> = ({
  level,
  color,
  style = 'gradient',
  align,
  className,
  children,
  animate = true,
  ...props
}) => {
  // Map heading level to text variant, ensuring it's valid
  const validLevel = typeof level === 'number' && level >= 1 && level <= 6 ? level : 2;
  const headingVariant = `h${validLevel}` as TextVariant;
  
  try {
    // Use the Text component with heading settings
    return (
      <Text
        variant={headingVariant}
        color={color || 'default'}
        style={style}
        align={align}
        className={className}
        animate={animate}
        {...props}
      >
        {children}
      </Text>
    );
  } catch (error) {
    console.error('Error rendering Heading component:', error);
    // Fallback rendering if Text component fails
    const HeadingTag = `h${validLevel}` as keyof JSX.IntrinsicElements;
    // Use type assertion to avoid type errors with props spreading
    const safeProps = {
      className,
      children,
      // Only include safe HTML attributes
      ...Object.entries(props).reduce((acc, [key, value]) => {
        if (['id', 'title', 'role', 'tabIndex', 'aria-label'].includes(key)) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>)
    };
    
    return React.createElement(HeadingTag, safeProps);
  }
};
